import {Formik, Form, Field} from 'formik'
import React, {useEffect, useState} from 'react'
import * as Yup from 'yup'
import {useIntl} from 'react-intl'
import TextInput from '../../../widgets/components/Input/TextInput'
import {SelectInput} from '../../../widgets/components/Input/SelectInput'
import deleteIcon from '../../../../../media/stakeholders/ic-delete.svg'
import {useThemeMode} from '../../../../../_metronic/partials'
import {useAuth} from '../../../auth'
import {createSettings, deleteSettings, getRecords} from '../../core/_requests'
import TagifyInput from '../../../shared-access/components/tagify-input'
import {viewableFileTypes} from '../../../../core/_constants'
import {toast} from 'react-toastify'
import Skeleton from 'react-loading-skeleton'
import {useSettingsData} from '../../../../hooks/getSettingsData'
import {InfoCard2} from '../../../widgets/components/UI/InfoCard2'
import {SettingsInputPeriod} from '../../../widgets/components/Input/SettingsInputPeriod'

const GeneralSettings = () => {
  const {formatMessage} = useIntl()
  const {companyId} = useAuth()
  const {mode} = useThemeMode()

  const [loading, setLoading] = useState<boolean>(false)
  const [disabledFileTypes, setDisabledFileTypes] = useState<string[]>([])
  const {settingsData, fileLimits, aiEmailLoading, setFileLimits} = useSettingsData()
  const [isBucketDisabled, setIsBucketDisabled] = useState<boolean>(false)
  const [isEmailValid, setIsEmailValid] = useState<boolean>(true)
  const [isNamingValid, setIsNamingValid] = useState<boolean>(true)
  const requiredSubstrings = ['level1', 'level2', 'level3', 'level4', 'level5']
  useEffect(() => {
    const updatedFilteredTypes = viewableFileTypes.filter(
      (type: string) => !fileLimits.some((limit) => limit.name === type)
    )

    setDisabledFileTypes(updatedFilteredTypes)
  }, [fileLimits])

  const getFileCounts = async () => {
    if (!companyId) return
    try {
      const {
        data: {data, success},
      } = await getRecords(companyId)
      if (success) {
        if (data) {
          setIsBucketDisabled(true)
        } else {
          setIsBucketDisabled(false)
        }
      }
    } catch (err) {
      console.log(err)
    }
  }

  useEffect(() => {
    getFileCounts()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const initialValues = {
    aiEmail: settingsData?.email || '',
    recordKeeperEmails: settingsData?.whiteListedEmail || '',
    financialYear: settingsData?.financialYear || '',
    isAutoTaggingEnabled: settingsData?.enableTagging || false,
    isAutoRenamingEnabled: settingsData?.enableRenaming || false,
    aiTags: settingsData?.tags || '',
    conventionPattern: settingsData?.namingConvention || '',
    bucketRegion: settingsData?.storageRegion || '',
    ...fileLimits.reduce((acc: Record<string, string | number>, limit, index) => {
      acc[`fileType${index + 1}`] = limit.name || ''
      acc[`maxSize${index + 1}`] = limit.maxFileSizeValue || ''
      acc[`fileTypeExt${index + 1}`] = limit.maxFileSize || ''
      return acc
    }, {}),
  }

  const addFileLimit = () => {
    setFileLimits([
      ...fileLimits,
      {
        name: '',
        type: 'FILE',
        maxFileSizeValue: '',
        maxFileSize: '',
      },
    ])
  }

  const deleteFileLimit = (index: any, id: string) => {
    const newLimits = fileLimits.filter((_, i) => i !== index)
    setFileLimits(newLimits)
    if (!companyId || !id) return
    deleteSettings(companyId, id, true)
  }

  const validationSchema = Yup.object().shape({
    recordKeeperEmails: Yup.string().test(
      'valid-emails',
      formatMessage({id: 'One or more emails are invalid'}),
      (value) => {
        if (!value) {
          setIsEmailValid(true)
          return true
        }
        const emails = value.split(',').map((email) => email.trim())
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        setIsEmailValid(emails.every((email) => emailRegex.test(email)))
        return emails.every((email) => emailRegex.test(email))
      }
    ),
    conventionPattern: Yup.string().test(
      'valid-pattern',
      formatMessage({id: 'Invalid convention pattern'}),
      (value) => {
        if (!value) {
          setIsNamingValid(true)
          return false
        }
        setIsNamingValid(
          requiredSubstrings.every((substring) => value.toLowerCase().includes(substring))
        )
        return requiredSubstrings.every((substring) => value.toLowerCase().includes(substring))
      }
    ),
    ...(fileLimits || []).reduce(
      (acc, _, index) => {
        acc[`maxSize${index + 1}`] = Yup.number()
          .required(formatMessage({id: 'Max Size is Required'}))
          .min(1,formatMessage({id: "Max Size cann't be negative or 0"}))
        acc[`fileTypeExt${index + 1}`] = Yup.string().required(
          formatMessage({id: 'File Type Extension is required'})
        )
        acc[`fileType${index + 1}`] = Yup.string().nullable() // Adjust as needed
        return acc
      },
      {} as Record<string, any> // Explicitly type the accumulator for spread
    ),
  })

  const handleSubmit = async (values: any, {resetForm}: any) => {
    if (!companyId) return
    setLoading(true)
    const payload = {
      whitelistedEmails: values?.recordKeeperEmails,
      financialYear: values?.financialYear,
      files: fileLimits,
      enableTagging: values?.isAutoTaggingEnabled,
      enableRenaming: values?.isAutoRenamingEnabled,
      tags: values?.aiTags,
      namingConvention: values?.conventionPattern,
      storageRegion: values?.bucketRegion,
    }
    try {
      const {
        data: {success, errors},
      } = await createSettings(companyId, payload)
      if (success) {
        toast.success('Settings saved successfully')
      } else {
        toast.error(errors[0])
      }
    } catch (err) {
    } finally {
      setLoading(false)
    }
  }

  const bucketOptions = [
    {id: 'usEastNVirginia', name: 'US East (N. Virginia)', value: 'usEastNVirginia'},
    {id: 'europeIreland', name: 'Europe (Ireland)', value: 'europeIreland'},
    {id: 'europeFrankfurt', name: 'Europe (Frankfurt)', value: 'europeFrankfurt'},
    {id: 'asiaPacificMumbai', name: 'Asia Pacific (Mumbai)', value: 'asiaPacificMumbai'},
    {id: 'asiaPacificSingapore', name: 'Asia Pacific (Singapore)', value: 'asiaPacificSingapore'},
    {id: 'middleEastBahrain', name: 'Middle East (Bahrain)', value: 'middleEastBahrain'},
    {id: 'southAmericaSaoPaulo', name: 'South America (São Paulo)', value: 'southAmericaSaoPaulo'},
    {id: 'africaCapeTown', name: 'Africa (Cape Town)', value: 'africaCapeTown'},
  ]
  console.log(fileLimits)

  return (
    <>
      <div className='mt-4'>
        <InfoCard2
          title={'Configuring General Settings for RecordsKeeper'}
          desc={
            'This page allows you to customize important settings such as file size limits, email forwarding, tagging, and renaming rules for better document management. Adjust these settings to streamline how records are handled in your system, ensuring compliance and consistency across all files.'
          }
          slug={
            'https://help.recordskeeper.ai/en/articles/10410259-how-to-configure-general-settings-for-your-recordskeeper-ai-account'
          }
        />
      </div>
      <div className='bg-white rounded p-4 mb-8'>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
          validateOnChange
          validateOnMount
        >
          {(formik) => {
            return (
              <Form>
                <div className='row'>
                  <div className='col-md-6 col-12'>
                    {aiEmailLoading ? (
                      <>
                        <div className='w-100'>
                          <Skeleton
                            height={20}
                            width={250}
                            baseColor='var(--kt-skeleton-bg-color)'
                            highlightColor='var(--kt-skeleton-highlight-bg-color)'
                          />
                        </div>
                        <div className='mt-2'>
                          <Skeleton
                            height={30}
                            baseColor='var(--kt-skeleton-bg-color)'
                            highlightColor='var(--kt-skeleton-highlight-bg-color)'
                          />
                        </div>
                      </>
                    ) : (
                      <TextInput
                        fieldType='text'
                        label={formatMessage({id: 'RecordsKeeper AI’s email ID.'})}
                        fieldName='aiEmail'
                        formik={formik}
                        placeholder={formatMessage({id: 'Enter email ID'})}
                        toolTipText={formatMessage({
                          id: 'This is the system-generated email address for RecordsKeeper AI. Send or forward emails with attachments to this address to automatically add the files to RecordsKeeper.',
                        })}
                        isStarRequired={true}
                        isTooltipForModal={true}
                        isDisabled
                        isClickToCopy
                      />
                    )}
                  </div>

                  <div className='col-md-6 col-12'>
                    {aiEmailLoading ? (
                      <>
                        <div className='w-100 mb-3'>
                          <Skeleton
                            height={20}
                            width={250}
                            baseColor='var(--kt-skeleton-bg-color)'
                            highlightColor='var(--kt-skeleton-highlight-bg-color)'
                          />
                        </div>
                        <div className='mt-2'>
                          <Skeleton
                            height={30}
                            baseColor='var(--kt-skeleton-bg-color)'
                            highlightColor='var(--kt-skeleton-highlight-bg-color)'
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <TagifyInput
                          label={formatMessage({id: 'Accept Records From These Email ids Only'})}
                          placeholder={formatMessage({id: 'Enter email IDs'})}
                          tooltip={formatMessage({
                            id: 'RecordsKeeper AI will only accept files and records via attachments from the specified email IDs. Any emails sent from other email addresses will be ignored.',
                          })}
                          fieldName='recordKeeperEmails'
                          formik={formik}
                        />
                        <div className='text-danger'>
                          {!isEmailValid &&
                            formik.getFieldProps('recordKeeperEmails').value !== null && (
                              <span>{'Please enter valid email address'}</span>
                            )}
                        </div>
                      </>
                    )}
                  </div>

                  <p className='mb-8'>
                    {formatMessage({
                      id: `Any team member can forward emails to the above email ID to automatically add the email's attachments as files or records in RecordsKeeper.`,
                    })}
                  </p>
                  <div className='col-md-6 col-12'>
                    {aiEmailLoading ? (
                      <>
                        <div className='w-100'>
                          <Skeleton
                            height={20}
                            width={250}
                            baseColor='var(--kt-skeleton-bg-color)'
                            highlightColor='var(--kt-skeleton-highlight-bg-color)'
                          />
                        </div>
                        <div className='mt-2 mb-3'>
                          <Skeleton
                            height={30}
                            baseColor='var(--kt-skeleton-bg-color)'
                            highlightColor='var(--kt-skeleton-highlight-bg-color)'
                          />
                        </div>
                      </>
                    ) : (
                      <SelectInput
                        label={formatMessage({id: 'Financial Year'})}
                        fieldName='financialYear'
                        placeholder={formatMessage({id: 'Select Financial Year'})}
                        formik={formik}
                        toolTipText={formatMessage({
                          id: 'Select the financial year cycle for your organization. This will be used to categorize documents based on your fiscal reporting periods.',
                        })}
                        options={[
                          {id: 1, name: 'April-March', value: 'April-March'},
                          {id: 2, name: 'May-April', value: 'May-April'},
                          {id: 3, name: 'June-May', value: 'June-May'},
                          {id: 4, name: 'July-June', value: 'July-June'},
                          {id: 5, name: 'August-July', value: 'August-July'},
                          {id: 6, name: 'September-August', value: 'September-August'},
                          {id: 7, name: 'October-September', value: 'October-September'},
                          {id: 8, name: 'November-October', value: 'November-October'},
                          {id: 9, name: 'December-November', value: 'December-November'},
                          {id: 11, name: 'January-December', value: 'January-December'},
                          {id: 11, name: 'February-January', value: 'February-January'},
                          {id: 12, name: 'March-February', value: 'March-February'},
                        ]}
                        isTooltipForModal={true}
                      />
                    )}
                  </div>

                  <div className='col-md-6 col-12'>
                    {aiEmailLoading ? (
                      <>
                        <div className='w-100'>
                          <Skeleton
                            height={20}
                            width={250}
                            baseColor='var(--kt-skeleton-bg-color)'
                            highlightColor='var(--kt-skeleton-highlight-bg-color)'
                          />
                        </div>
                        <div className='mt-2 mb-3'>
                          <Skeleton
                            height={30}
                            baseColor='var(--kt-skeleton-bg-color)'
                            highlightColor='var(--kt-skeleton-highlight-bg-color)'
                          />
                        </div>
                      </>
                    ) : (
                      <SelectInput
                        label={formatMessage({id: 'Bucket storage region'})}
                        fieldName='bucketRegion'
                        placeholder={formatMessage({id: 'Select bucket region'})}
                        formik={formik}
                        toolTipText={formatMessage({
                          id: 'Select the bucket storage region.',
                        })}
                        options={bucketOptions}
                        isTooltipForModal={true}
                        disabled={isBucketDisabled}
                      />
                    )}
                  </div>
                  <div className='row'>
                    <div></div>
                    <div
                      className='col-lg-12 mb-8 mx-3'
                      style={{borderBottom: '1px dotted #DBDFE9'}}
                    ></div>
                    <div></div>
                  </div>
                  <div className='mb-8'>
                    <p className='fw-semibold'>
                      {formatMessage({id: 'Set Max File Size by File Type'})}
                    </p>
                    <div
                      className='p-2 rounded'
                      //  style={{ border: '2px dashed #FFC700' }}
                      style={
                        mode === 'dark'
                          ? {background: '#6C4F2C', border: '2px dashed #FFC700'}
                          : {background: '#FFF8DD', border: '2px dashed #FFC700'}
                      }
                    >
                      <p className='m-0 text-black'>
                        <span className='fw-bold'>{formatMessage({id: 'Note: '})}</span>
                        {formatMessage({
                          id: 'In case of a file size conflict, the smaller limit will apply. For example, if the department file size limit is set to 100MB but the limit for PDF files is 50MB, the maximum file size allowed for PDFs in the Finance department will be 50MB.',
                        })}
                      </p>
                    </div>
                  </div>
                  {aiEmailLoading ? (
                    <div className='d-flex flex-row mb-3'>
                      <div className='w-25 me-5'>
                        <Skeleton
                          height={30}
                          baseColor='var(--kt-skeleton-bg-color)'
                          highlightColor='var(--kt-skeleton-highlight-bg-color)'
                        />
                      </div>
                      <div className='w-25 me-5'>
                        <Skeleton
                          height={30}
                          baseColor='var(--kt-skeleton-bg-color)'
                          highlightColor='var(--kt-skeleton-highlight-bg-color)'
                        />
                      </div>
                      <div className='w-25'>
                        <Skeleton
                          height={30}
                          baseColor='var(--kt-skeleton-bg-color)'
                          highlightColor='var(--kt-skeleton-highlight-bg-color)'
                        />
                      </div>
                    </div>
                  ) : (
                    fileLimits.map((limit: any, index) => (
                      <>
                        <div key={index} className='row mb-8 mb-md-0'>
                          <div className='col-md-5 col-12'>
                            <SelectInput
                              label={formatMessage({id: 'File Type'})}
                              fieldName={`fileType${index + 1}`}
                              placeholder={formatMessage({id: 'Select File Type'})}
                              formik={formik}
                              toolTipText={formatMessage({
                                id: 'Choose the file type for which you want to set a size limit...',
                              })}
                              onChange={(event) => {
                                const value = event.target.value

                                setFileLimits((prevLimits) => {
                                  const newLimits = [...prevLimits]
                                  newLimits[index] = {
                                    ...newLimits[index],
                                    name: value,
                                  }
                                  return newLimits
                                })
                              }}
                              options={viewableFileTypes}
                              isTooltipForModal={true}
                              disabledFileTypes={disabledFileTypes}
                            />
                          </div>
                          <div className='col-md-6 col-12'>
                            <SettingsInputPeriod
                              label={formatMessage({
                                id: 'Max File Size',
                              })}
                              toolTipText={formatMessage({
                                id: 'Enter the maximum file size limit for this specific file type...',
                              })}
                              fieldName={`maxSize${index + 1}`}
                              placeholder={''}
                              placeholderSelect={''}
                              formik={formik}
                              isTooltipForModal={true}
                              options={[
                                {id: 1, name: 'KB', value: 'KB'},
                                {id: 2, name: 'MB', value: 'MB'},
                                {id: 3, name: 'GB', value: 'GB'},
                              ]}
                              selectOptionFieldName={`fileTypeExt${index + 1}`}
                              onChange={(event: any) => {
                                const value = event.value
                                setFileLimits((prevLimits: any) => {
                                  const newLimits = [...prevLimits]
                                  newLimits[index] = {
                                    ...newLimits[index],
                                    maxFileSize: value,
                                  }
                                  return newLimits
                                })
                              }}
                              onInputChange={(event: any) => {
                                const value = event.target.value
                                setFileLimits((prevLimits: any) => {
                                  const newLimits = [...prevLimits]
                                  newLimits[index] = {
                                    ...newLimits[index],
                                    maxFileSizeValue: value
                                  }
                                  return newLimits
                                })
                              }}
                            />
                          </div>
                          <div className='col-1 col-md-1 align-self-center'>
                            <img
                              src={deleteIcon}
                              alt='delete'
                              className='cursor-pointer'
                              width={32}
                              height={32}
                              onClick={() => deleteFileLimit(index, limit.id)}
                            />
                          </div>
                        </div>
                      </>
                    ))
                  )}

                  <div>
                    <button
                      type='button'
                      className='btn btn-primary'
                      onClick={() => addFileLimit()}
                    >
                      {formatMessage({id: '+ Add More Filetype Size Limit'})}
                    </button>
                  </div>

                  <div className='row'>
                    <div></div>
                    <div
                      className='col-lg-12 mb-8 mx-3 my-8'
                      style={{borderBottom: '1px dotted #DBDFE9'}}
                    ></div>
                    <div></div>
                  </div>
                  <div className='col-md-6 col-12'>
                    <div className='form-check form-check-custom form-check-solid form-check-primary form-switch'>
                      <label className='togglebtn mx-0'>
                        <Field
                          className='togglebtn__input'
                          name='isAutoRenamingEnabled'
                          type='checkbox'
                          formik={formik}
                          onChange={(e: any) => {
                            const isChecked = e.target.checked
                            console.log(isChecked, 'ischecked')
                            formik.setFieldValue('isAutoRenamingEnabled', isChecked)
                          }}
                        />
                        <div className='togglebtn__fill'></div>
                      </label>
                      <div className='d-flex flex-column justify-content-end mt-2'>
                        <div className='d-flex'>
                          <div>
                            <label
                              className='form-check-label fw-semibold font-size-13 title-notification'
                              data-bs-toggle='tooltip'
                            >
                              {formatMessage({id: 'Enable Auto-Renaming'})}
                            </label>
                          </div>
                        </div>
                        <label
                          className='form-check-label text-clr58 fw-normal fs-7'
                          data-bs-toggle='tooltip'
                        >
                          {formatMessage({
                            id: 'Turn this on to automatically rename files using a predefined naming convention, improving consistency and searchability.',
                          })}
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className='col-md-6 col-12'>
                    {aiEmailLoading ? (
                      <div className='mt-2'>
                        <Skeleton
                          height={30}
                          baseColor='var(--kt-skeleton-bg-color)'
                          highlightColor='var(--kt-skeleton-highlight-bg-color)'
                        />
                      </div>
                    ) : (
                      <>
                        <TextInput
                          fieldType='text'
                          label={formatMessage({id: 'Naming Convention Pattern'})}
                          fieldName='conventionPattern'
                          formik={formik}
                          placeholder={formatMessage({
                            id: '[Level 1]-[Level 2]-[Level 3]-[Level 4]-[Level 5]',
                          })}
                          toolTipText={formatMessage({
                            id: 'Set a standardized naming convention for auto-renaming files. Use placeholders such as Level 1, Level 2 to reflect your file structure hierarchy.',
                          })}
                          isStarRequired={true}
                          isTooltipForModal={true}
                          isDisabled={!formik.values.isAutoRenamingEnabled}
                        />
                        <div className='text-danger'>
                          {!isNamingValid && formik.getFieldProps('conventionPattern').value ? (
                            <span>
                              {(() => {
                                const missingSubstring = requiredSubstrings.findIndex(
                                  (substring) =>
                                    !formik
                                      .getFieldProps('conventionPattern')
                                      .value.toLowerCase()
                                      .includes(substring)
                                )
                                return missingSubstring
                                  ? `${requiredSubstrings[missingSubstring]} is missing`
                                  : null
                              })()}
                            </span>
                          ) : (
                            <>
                              {formik.getFieldProps('conventionPattern').value === null && (
                                <span>{'Level1 is missing'}</span>
                              )}
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  <div className='d-flex justify-content-end'>
                    <button
                      type='submit'
                      className='btn bg-primary text-white custom-button-property border border-1 mb-md-4 mb-0 px-6 py-4 text-center w-100 w-md-auto order-1 order-md-2'
                      disabled={!formik.isValid || loading}
                    >
                      {!loading && (
                        <span className='indicator-label font-size-13 p-0'>
                          {formatMessage({id: 'Save Changes'})}
                        </span>
                      )}
                      {loading && (
                        <span className='indicator-label font-size-13 p-0'>
                          {formatMessage({id: 'Please wait...'})}
                          <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              </Form>
            )
          }}
        </Formik>
      </div>
    </>
  )
}

export default GeneralSettings
